import TrimFrame from '@/components/app/document/overlays/trim-frame'
import type { PageViewMode } from '@/components/pages/home'
import { computeHalfTrimWidth, computeSnappedPageTrimWidth } from '../helpers/page-trim-width'

const TRIM_ABOVE_SHAPES_Z = 10

type CanvasTrimFrameOverlayAboveShapesProps = {
  isSinglePage: boolean
  pageViewMode: PageViewMode
  bleedWidth: number
  bleedMargin: number
  trimWidth: number
  trimHeight: number
  /**
   * Double-spread only: shared, snapped spine seam (ruler "0" / spine bar / page-trim split in
   * `double-spread-document.tsx`) — keeps the dashed trim split at the same seam those draw at,
   * instead of an independently-floored approximation that can drift a fraction of a point off it.
   */
  spineSnapCanvasX?: number
}

/**
 * Dashed trim in `center-canvas` above `ShapeManager` / `TextManager` / bleed tint (Figma: trim
 * reads over content). For a double spread, the spine-facing side of each page half is hidden since
 * the two halves meet there and only one shared line should show. A standalone first/last single
 * page has no facing page in view, so all four of its sides — including the spine-facing one — are
 * real trim edges and are drawn.
 */
export function CanvasTrimFrameOverlayAboveShapes({
  isSinglePage,
  pageViewMode,
  bleedWidth,
  bleedMargin,
  trimWidth,
  trimHeight,
  spineSnapCanvasX,
}: CanvasTrimFrameOverlayAboveShapesProps) {
  const rawPageTrimWidth = computeHalfTrimWidth(trimWidth)

  if (rawPageTrimWidth <= 0 || trimHeight <= 0) {
    return null
  }

  if (!isSinglePage) {
    const leftTrimLeft = bleedMargin
    const pageTrimWidth = computeSnappedPageTrimWidth(leftTrimLeft, trimWidth, spineSnapCanvasX)
    const rightTrimLeft = leftTrimLeft + pageTrimWidth

    return (
      <>
        <TrimFrame
          left={leftTrimLeft}
          top={bleedMargin}
          width={pageTrimWidth}
          height={trimHeight}
          hideLeft={false}
          hideRight
          zIndex={TRIM_ABOVE_SHAPES_Z}
        />
        <TrimFrame
          left={rightTrimLeft}
          top={bleedMargin}
          width={pageTrimWidth}
          height={trimHeight}
          hideLeft
          hideRight={false}
          zIndex={TRIM_ABOVE_SHAPES_Z}
        />
      </>
    )
  }

  const halfSpread = Math.floor(bleedWidth / 2)
  const isRightHalf = pageViewMode === 'single-first'
  const canvasTrimLeft = isRightHalf ? halfSpread : bleedMargin

  return (
    <TrimFrame
      left={canvasTrimLeft}
      top={bleedMargin}
      width={rawPageTrimWidth}
      height={trimHeight}
      zIndex={TRIM_ABOVE_SHAPES_Z}
    />
  )
}
